/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';

import { Link1 } from '@ui-library/typography';
import { ISource } from '@stores/source';
import { TAB_NAMES } from '@components/sources/source-view/utils';
import CloudSourceAuth from '@components/sources/source/cloudSource/cloudSourceAuth/cloudSourceAuth';
import CloudSourceSettings from '@components/sources/source/cloudSource/cloudSourceSettings';
import { DIRECTORY_TABS, URLS } from '@components/common/constants';
import { ISourceDataStore } from '@stores/sourceDataStore';
import { IFeaturesStore } from '@stores/features';
import { experiment, FeatureFlagKeys } from '@lib/experiment';

import {
  CredentialsAuth,
  SourceCategory,
  AccountValidator,
  DirectoryTab,
  GetWebhookUrlOptions,
} from './sourceCategory';
import { ExtractSourceAccountManagement } from '../source-view/source-settings/components/credentials/extractSourceAccountManagement';

class CloudSource extends SourceCategory {
  override isNewConnectionAllowed(source: { destinations: unknown[] }): boolean {
    return source.destinations.length === 0;
  }

  override isCreateAllowed(definitionName: string, featuresStore: IFeaturesStore) {
    const enableETLSunset = experiment.isFeatureEnabled(FeatureFlagKeys.enableETLSunset);
    const { useAllowlistForCreation } = experiment.getFeatureValue(FeatureFlagKeys.enableETLSunset);
    const etlFeature = featuresStore.getFeatureConfiguration('ETL') as boolean | undefined;
    const etlAllowlist = featuresStore.getFeatureConfiguration('ETL_ALLOWLIST') as
      | string[]
      | undefined;

    // Creation of a new ETL source is not allowed if
    // - enableETLSunset is false and ETL feature value is true OR
    // - enableETLSunset is true, useAllowlistForCreation is true and ETL_ALLOWLIST contains the source definition name
    return (
      (!enableETLSunset && !!etlFeature) ||
      (enableETLSunset &&
        useAllowlistForCreation &&
        !!etlAllowlist &&
        etlAllowlist.includes(definitionName))
    );
  }

  override getSunsetReason(definitionName: string, featuresStore: IFeaturesStore) {
    const enableETLSunset = experiment.isFeatureEnabled(FeatureFlagKeys.enableETLSunset);
    const etlAllowlist = featuresStore.getFeatureConfiguration('ETL_ALLOWLIST') as
      | string[]
      | undefined;

    // Source is sunsetted if:
    // - enableETLSunset is true and ETL_ALLOWLIST does not contain the source definition name
    const isSunset = enableETLSunset && !etlAllowlist?.includes(definitionName);

    return isSunset ? (
      <>
        Per our deprecation policy for ETL, this source can no longer be edited.{' '}
        <Link1 variant="dark" href={URLS.ETL_SUNSET_RELEASE_NOTES} target="_blank" rel="noreferrer">
          Learn more
        </Link1>
      </>
    ) : null;
  }

  override getWebhookUrl(_: GetWebhookUrlOptions) {
    return null;
  }

  override async validateAccount({
    source,
    isNewAccountName,
    setCredentials,
    showErrorMessage,
    accountInfo,
    isOauthSource,
  }: AccountValidator) {
    if (!isOauthSource(source?.sourceDef!)) {
      if (await isNewAccountName(String(accountInfo?.options?.rudder_account_name))) {
        const skipValidation = source?.sourceDef.name === 'marketo';
        const account = await source!.createAccount({ ...accountInfo, skipValidation });
        setCredentials({ accountId: account.id, accountInfo: account, valid: true, updated: true });
        return account.id;
      }
      showErrorMessage?.(
        `Account Name ${accountInfo?.options?.rudder_account_name} already exists`,
      );
    }
    return undefined;
  }

  override handleSetup(canCreateCloud: boolean, _: boolean, create: () => void) {
    if (canCreateCloud) {
      create();
      return true;
    }
    return false;
  }

  override getAccountsEndpoint(sourceDef?: string) {
    const base = '/cloudSources/accounts';
    if (sourceDef) {
      return `${base}/${sourceDef}`;
    }
    return base;
  }

  override getSourceType(_: { name: string }) {
    return 'cloudSource';
  }

  override showTablePrefixSettings() {
    return true;
  }

  override showSyncScheduleSettings() {
    return true;
  }

  override isPartOf(tab: DirectoryTab) {
    return tab === DIRECTORY_TABS.CLOUD_EXTRACT;
  }

  override getAuthComponent({ handleChange, source, credentials }: CredentialsAuth) {
    return (
      <CloudSourceAuth
        onChange={handleChange}
        sourceDefinition={source?.sourceDef}
        selectedAccount={
          credentials?.accountId || source?.config?.rudderAccountId || source?.config?.accountId
        }
      />
    );
  }

  override getTabs(source: ISource) {
    if (CloudSourceSettings.configExists(source.sourceDef)) {
      return [
        TAB_NAMES.DESTINATIONS,
        TAB_NAMES.SYNCS,
        TAB_NAMES.CONFIGURATION,
        TAB_NAMES.RESOURCES,
        TAB_NAMES.SETTINGS,
      ];
    }
    return [TAB_NAMES.DESTINATIONS, TAB_NAMES.SYNCS, TAB_NAMES.RESOURCES, TAB_NAMES.SETTINGS];
  }

  override getCredentialSettings(_: ISourceDataStore): JSX.Element | null {
    return <ExtractSourceAccountManagement />;
  }
}

/* eslint-disable import/no-default-export */
export default CloudSource;
